import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TokenService } from '@app/auth/auth/token.service';
import { ProductoList } from '@app/entidades/operacion.model';
import { StorageService } from '@app/servicios/storage/storage.service';
import { environment } from '@env';
import { Subscription, timer } from 'rxjs';
import { Producto } from 'src/app/entidades/producto.model';
import { ProductoService } from 'src/app/servicios/producto/producto.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  public suscription: Subscription = new Subscription();

  fotoUrl = environment.servImgURL + '/';

  public rawProductosNuevos: Producto[] = [];
  public rawProductosMasVendidos: Producto[] = [];

  public nuevosSlides: Producto[][] = [];
  public masVendidosSlides: Producto[][] = [];

  public lista: ProductoList[] = [];
  mayorista = false;
  alertOk = false;

  @HostListener('window:resize', ['$event'])
  onWindowWidth() {
    this.updateSlides();
  }

  constructor(
    private productoServ: ProductoService,
    private tokenService: TokenService,
    private storageService: StorageService,
    private location: Location
  ) {}

  ngOnInit() {
    this.consultarProductosNuevos();
    this.consultarProductosMasVendidos();
    this.verificarSesion();
  }

  goBack() {
    this.location.back();
  }

  public verificarSesion() {
    this.mayorista = this.tokenService.isLoggedIn() && this.tokenService.notExpired();
  }

  consultarProductosNuevos() {
    const filter = {
      activo: 'ACTIVO',
      id: '',
      nombre: '',
      categoria: '',
      tipo: '',
      subtipo: '',
      codPrecio: '',
      esNuevo: 'NUEVO',
      visible: true,
      ordenarPor: 'ultimoIngreso',
    };

    const page = 0;
    const size = 12;

    this.suscription.add(
      this.productoServ.filtrarProductos(page, size, filter).subscribe({
        next: (resp) => {
          const data = (resp?.data || []).slice(0, 12);
          if (data.length > 0) {
            this.rawProductosNuevos = data;
            this.updateSlides();
          } else {
            // Fallback en caso de que no haya productos explícitamente marcados con esNuevo='NUEVO'
            this.consultarProductosRecientesFallback();
          }
        },
        error: (err) => {
          console.error('Error al consultar productos nuevos', err);
          this.consultarProductosRecientesFallback();
        },
      })
    );
  }

  private consultarProductosRecientesFallback() {
    const filter = {
      activo: 'ACTIVO',
      id: '',
      nombre: '',
      categoria: '',
      tipo: '',
      subtipo: '',
      codPrecio: '',
      esNuevo: '',
      visible: true,
      ordenarPor: 'ultimoIngreso',
    };

    this.suscription.add(
      this.productoServ.filtrarProductos(0, 12, filter).subscribe({
        next: (resp) => {
          this.rawProductosNuevos = (resp?.data || []).slice(0, 12);
          this.updateSlides();
        },
        error: (err) => {
          console.error('Error al consultar fallback de productos recientes', err);
          this.rawProductosNuevos = [];
          this.updateSlides();
        },
      })
    );
  }

  consultarProductosMasVendidos() {
    this.suscription.add(
      this.productoServ.consultarMasVendidos().subscribe({
        next: (resp) => {
          this.rawProductosMasVendidos = (resp?.data || []).slice(0, 12);
          this.updateSlides();
        },
        error: (err) => {
          console.error('Error al consultar más vendidos', err);
          this.rawProductosMasVendidos = [];
          this.updateSlides();
        },
      })
    );
  }

  getChunkSize(): number {
    const width = window.innerWidth;
    if (width < 640) return 1; // mobile: 1 card por slide
    if (width < 992) return 2; // tablet: 2 cards por slide
    if (width < 1280) return 3; // laptop: 3 cards por slide
    if (width < 1536) return 4; // desktop: 4 cards por slide
    return 5; // pantalla ancha: 5 cards por slide
  }

  updateSlides() {
    const chunkSize = this.getChunkSize();
    this.nuevosSlides = this.chunkArray(this.rawProductosNuevos, chunkSize);
    this.masVendidosSlides = this.chunkArray(this.rawProductosMasVendidos, chunkSize);
  }

  private chunkArray(items: Producto[], size: number): Producto[][] {
    if (!items || items.length === 0) return [];
    const chunks: Producto[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  }

  agregarProducto(producto: Producto) {
    if (!producto || !producto.stock) {
      return;
    }

    const maxAvailable = Number(producto.stock?.stock ?? 0);
    const localStock = Number(producto.stock?.local ?? 0);
    if (!Number.isFinite(maxAvailable) || maxAvailable <= 0 || !Number.isFinite(localStock) || localStock <= 0) {
      return;
    }

    this.lista = this.storageService.getItem('lp') || [];

    const existing = this.lista.find((p) => p.id === producto.id);
    if (existing) {
      if (existing.cantidad < maxAvailable) {
        existing.cantidad++;
      } else {
        return;
      }
    } else {
      let prod: ProductoList = {
        id: producto.id,
        nombre: producto.nombre,
        foto: this.fotoUrl + producto.foto,
        cantidad: 1,
        stock: maxAvailable,
        tipo: producto.tipo,
        precio: producto.precios,
      };

      this.lista.push(prod);
    }

    this.storageService.setItem('lp', this.lista);
    this.alertSuccess();
  }

  alertSuccess() {
    this.alertOk = true;
    timer(1500).subscribe(() => (this.alertOk = false));
  }

  ngOnDestroy(): void {
    this.suscription.unsubscribe();
  }
}
